import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import {
  PageComposerStateModel,
  PageComposerGroupByPayload,
  PageComposerAggregatePayload,
  PageComposerModel,
  PageParams,
} from '../../models/page-composer.model';
import { RootState } from '../index';

// identifier
export const COMPOSER_SLICE_ID = 'composer';

// default state
const initialState: PageComposerStateModel = {
  groupBy: {},
  filters: {},
  aggregates: {},
  page: null,
  widgetHost: null,
  widgetLoadState: {},
  pageParams: null,
};

//slice
const composerSlice = createSlice({
  name: COMPOSER_SLICE_ID,
  initialState,
  reducers: {
    setPage: (state: PageComposerStateModel, action: PayloadAction<PageComposerModel>): void => {
      state.page = action.payload;
    },
    setWidgetGroupBy: (
      state: PageComposerStateModel,
      action: PayloadAction<PageComposerGroupByPayload>
    ): void => {
      state.groupBy[action.payload.id] = action.payload.groupBy;
    },
    setGroupByBulk: (state: PageComposerStateModel, action: PayloadAction<any>): void => {
      state.groupBy = {
        ...state.groupBy,
        ...action.payload,
      };
    },
    setAggregateBulk: (state: PageComposerStateModel, action: PayloadAction<any>): void => {
      state.aggregates = {
        ...state.aggregates,
        ...action.payload,
      };
    },
    setWidgetAggregates: (
      state: PageComposerStateModel,
      action: PayloadAction<PageComposerAggregatePayload>
    ): void => {
      state.aggregates[action.payload.widgetId] = action.payload.aggregates;
    },
    resetPage: (state: PageComposerStateModel) => {
      state.page = null;
      state.pageParams = initialState.pageParams;
    },
    clearGroupAndAggreates: (state: PageComposerStateModel) => {
      state.aggregates = {};
      state.groupBy = {};
    },
    setHost: (state: PageComposerStateModel, action: PayloadAction<string>) => {
      state.widgetHost = action.payload;
    },
    setPageParams: (state: PageComposerStateModel, action: PayloadAction<PageParams>) => {
      state.pageParams = action.payload;
    },
    clearHost: (state: PageComposerStateModel) => {
      state.widgetHost = null;
    },
    resetWidgetLoadState: (state: PageComposerStateModel) => {
      state.widgetLoadState = {};
    },
  },
});

//selectors
export const selectWidgetGroupByById = (state: RootState, widgetId: string) =>
  (state.composer.groupBy[widgetId] as string) ?? null;

export const selectWidgetRequestFilter = (state: RootState) => state.composer.filters;
export const selectWidgetAggregates = (state: RootState) => state.composer.aggregates;

export const selectWidgetAggregatesById = (state: RootState, widgetId: string) =>
  state.composer.aggregates[widgetId]?.length ? state.composer.aggregates[widgetId] : null;

export const selectPage = (state: RootState) => state.composer.page;
export const selectHost = (state: RootState) => state.composer.widgetHost;
export const oldselectPageParams = (state: RootState) => state.composer.pageParams;
export const selectPageParams = createSelector(
  [(state: RootState) => state.composer.pageParams],
  (pageParams) => pageParams || {}
);

//actions
export const {
  setWidgetGroupBy,
  setWidgetAggregates,
  setPage,
  resetPage,
  setGroupByBulk,
  setAggregateBulk,
  clearGroupAndAggreates,
  setHost,
  clearHost,
  resetWidgetLoadState,
  setPageParams,
} = composerSlice.actions;

//reducer
export default composerSlice;
